import React, {useEffect} from 'react'
import {useSelector} from 'react-redux'
import styled from '@emotion/styled'
import {RoomsTopOfferPrice} from 'components/Accommodation/Rooms/SplitBookingCard/RoomsTopOfferPrice'
import {RoomSwitchRequiredTooltip} from 'components/Offer/Primary/RoomSwitchRequiredTooltip/RoomSwitchRequiredTooltip'
import {getRoomOfferAnalyticsContexts} from 'middleware/analytics/selectors'
import {getHasActiveRoomFilter} from 'modules/accommodation/selectors'
import {getSearchParameters} from 'modules/sapiSearch/selectors'
import {
  getAccommodationPageHotel,
  getIsRoomsPollingComplete,
  getRoomCardEntities,
  getRoomOfferAccessConfig
} from 'modules/searchApi/selectors'

import {Box} from '@vio/atlas/helpers/Box'
import {cssTheme} from '@vio/atlas/themes'
import {useInView} from '@vio/core/src/_web/utils/browser/hooks/useInView'
import {useDispatchTeamEvent} from '@vio/core/src/analytics/hooks/useDispatchTeamEvent'
import {trackEvent} from '@vio/core/src/analytics/modules/actions'
import {
  Action,
  Category,
  Entity,
  Page
} from '@vio/core/src/analytics/types/Events'
import {getShouldSeeOffersUnlocked} from '@vio/core/src/auth/modules/selectors'
import {
  getCancellationDeadline,
  getRefundType
} from '@vio/core/src/offer/business/cancellationPenalties'
import {getOfferPaymentMode} from '@vio/core/src/offer/business/offerPaymentMode'
import {Offer} from '@vio/core/src/offer/types/offer'
import {
  getDisplayPrice,
  getTotalPrice
} from '@vio/core/src/price/business/price'
import {getSplitBookingDates} from '@vio/core/src/room/business/utils/getSplitBookingDates'
import {getSplitBookingRooms} from '@vio/core/src/room/business/utils/getSplitBookingRooms'
import {RoomCardEntityType, SplitBooking} from '@vio/core/src/room/types/room'
import {OfferPerks} from '@vio/shared/src/accommodation/OfferPerks/OfferPerks'
import {EarnPoints} from '@vio/shared/src/whiteLabel/EarnPoints'
import {usePointsPriceDisplay} from '@vio/shared/src/whiteLabel/hooks/usePointsPriceDisplay'

import {AccommodationOfferBoxSoldout} from '../AccommodationOfferBox/AccommodationOfferBoxSoldout'
import {PrimaryRoomOfferCTA} from '../PrimaryRoomOffer/PrimaryRoomOfferCTA'
import {OtaOfferLoading} from './OtaOfferLoading'
import {OtaOfferPrice} from './OtaOfferPrice'
import {OtaOfferRoomName} from './OtaOfferRoomName'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${cssTheme.layout.spacing.s500};
  width: 100%;
`

interface Props {
  hotelId: string
  component: string
}

export const OtaOffer = ({hotelId, component}: Props) => {
  const dispatchTeamEvent = useDispatchTeamEvent()
  const {inView, ref} = useInView({triggerOnce: true, threshold: 0.5})

  const shouldSeeOffersUnlocked = useSelector(getShouldSeeOffersUnlocked)
  const isRoomsPollingComplete = useSelector(getIsRoomsPollingComplete)
  const {checkIn, checkOut} = useSelector(getSearchParameters) || {}
  const hasActiveFilters = useSelector(getHasActiveRoomFilter)
  const hotel = useSelector(getAccommodationPageHotel)
  const {canEarn} = usePointsPriceDisplay()
  const entities = useSelector(getRoomCardEntities)
  const pickedEntity = entities[0]
  const isSplitBookingPicked =
    pickedEntity?.type === RoomCardEntityType.SplitBooking
  // A room entity can exist with an empty offers list, so the resolved offer -
  // not the entity - decides whether there is anything to show.
  const pickedOffer: Offer | SplitBooking | undefined =
    pickedEntity?.type === RoomCardEntityType.SplitBooking
      ? pickedEntity.data
      : pickedEntity?.data.offers?.[0]

  const analyticsContext = useSelector(state =>
    getRoomOfferAnalyticsContexts(
      state,
      isSplitBookingPicked
        ? (pickedOffer as SplitBooking)?.bundleId
        : (pickedOffer as Offer)?.id,
      isSplitBookingPicked
    )
  )
  const flowType = useSelector(state =>
    getRoomOfferAccessConfig(
      state,
      isSplitBookingPicked ? undefined : (pickedOffer as Offer)?.id
    )
  )
  const payload = {
    isOfferLocked: false,
    shouldSeeOffersUnlocked
  }

  const trackOfferClicked = () => {
    dispatchTeamEvent(
      trackEvent({
        category: Category.User,
        entity: Entity.Offer,
        action: Action.Clicked,
        page: Page.Accommodation,
        component,
        analyticsContext,
        payload
      })
    )
  }

  useEffect(() => {
    if (inView)
      dispatchTeamEvent(
        trackEvent({
          category: Category.User,
          entity: Entity.Offer,
          action: Action.Viewed,
          page: Page.Accommodation,
          component,
          analyticsContext,
          payload
        })
      )
  }, [inView])

  if (!pickedOffer) {
    if (!isRoomsPollingComplete) {
      return <OtaOfferLoading />
    }
    return (
      <AccommodationOfferBoxSoldout
        checkIn={checkIn}
        checkOut={checkOut}
        hasActiveFilters={hasActiveFilters}
      />
    )
  }

  // Bundle-level penalties describe both stays. The meal plan is per-stay, so
  // it is not claimed for the bundle, matching the other split-booking cards.
  const perks = isSplitBookingPicked
    ? {
        cancellationPenalties: pickedEntity.data.cancellationPenalties || [],
        paymentType: getOfferPaymentMode({isSplitBooking: true}),
        mealPlan: undefined
      }
    : {
        cancellationPenalties: (pickedOffer as Offer).cancellationPenalties,
        paymentType: getOfferPaymentMode({offer: pickedOffer as Offer}),
        mealPlan: (pickedOffer as Offer).mealPlan
      }

  return (
    <Container ref={ref} data-id="OtaOffer">
      <Box
        display="flex"
        flexDirection="column"
        gap={cssTheme.layout.spacing.s300}
      >
        {isSplitBookingPicked ? null : (
          <OtaOfferRoomName name={pickedEntity.data.name} />
        )}
        <OfferPerks
          paymentType={perks.paymentType}
          refundType={getRefundType(perks.cancellationPenalties || [])}
          cancellationDeadline={getCancellationDeadline(
            perks.cancellationPenalties
          )}
          timezone={hotel?.timezone}
          mealPlan={perks.mealPlan}
        />
        {isSplitBookingPicked ? (
          <RoomSwitchRequiredTooltip colorPath="content.neutral.secondary" />
        ) : null}
      </Box>
      <Box display="flex" flexDirection="column">
        {isSplitBookingPicked ? (
          <RoomsTopOfferPrice
            hotelId={hotelId}
            prices={pickedOffer.prices}
            splitBookingDates={getSplitBookingDates(pickedEntity.data)}
            splitBookingRooms={getSplitBookingRooms(pickedEntity.data)}
            isCompact
          />
        ) : (
          <OtaOfferPrice hotelId={hotelId} offer={pickedOffer as Offer} />
        )}
        {canEarn && (
          <Box paddingTop="s300">
            <EarnPoints
              amount={getTotalPrice(getDisplayPrice(pickedOffer.prices ?? []))}
            />
          </Box>
        )}
        <PrimaryRoomOfferCTA
          trackOfferClicked={trackOfferClicked}
          flowType={flowType}
        />
      </Box>
    </Container>
  )
}
